import { useState } from "react";

import type {
  BannerLibraryFilterDraft,
  BannerLibraryTranslations,
} from "./types";

import { Button as ButtonV2 } from "../../Button";
import { useIsMobile } from "../../hooks/useIsMobile";
import { BadgeIcon, FilterIcon } from "../../Icons";
import { MultiSelect, type MultiSelectOption } from "../../MultiSelect";
import { ResponsiveModal } from "../../ResponsiveModal";
import { EMPTY_BANNER_LIBRARY_FILTER } from "./types";

type BannerLibraryFilterProps = {
  applied: BannerLibraryFilterDraft;
  onApply: (draft: BannerLibraryFilterDraft) => void;
  languageOptions: MultiSelectOption[];
  sizeOptions: MultiSelectOption[];
  translations: BannerLibraryTranslations;
  /** Fired when the filter button is clicked, opening the modal. */
  onFilterButtonClick?: () => void;
  /** Fired for each newly-selected language option. */
  onLanguageChange?: (option: string) => void;
  /** Fired for each newly-selected size option. */
  onSizeChange?: (option: string) => void;
  /** Fired with the draft being applied, before it's committed. */
  onApplyClick?: (draft: BannerLibraryFilterDraft) => void;
  /** Fired when the Clear button is clicked. */
  onClearClick?: () => void;
};

/** Track only the options newly added to the selection (present in `next`, absent from `previous`). */
const trackMultiSelectChange = (
  previous: MultiSelectOption[],
  next: MultiSelectOption[],
  track: (option: string) => void,
) => {
  const previousValues = new Set(previous.map((o) => o.value));
  next
    .filter((o) => !previousValues.has(o.value))
    .forEach((o) => track(o.label));
};

export const BannerLibraryFilter = ({
  applied,
  onApply,
  languageOptions,
  sizeOptions,
  translations: t,
  onFilterButtonClick,
  onLanguageChange,
  onSizeChange,
  onApplyClick,
  onClearClick,
}: BannerLibraryFilterProps) => {
  const isMobile = useIsMobile();

  const [isOpen, setIsOpen] = useState(false);
  const [draft, setDraft] = useState<BannerLibraryFilterDraft>(
    EMPTY_BANNER_LIBRARY_FILTER,
  );

  const activeFilterCount =
    (applied.language.length > 0 ? 1 : 0) +
    (applied.bannerSize.length > 0 ? 1 : 0);

  const openFilter = () => {
    onFilterButtonClick?.();
    setDraft(applied);
    setIsOpen(true);
  };

  const closeFilter = () => setIsOpen(false);

  const applyFilters = () => {
    onApplyClick?.(draft);
    onApply(draft);
    setIsOpen(false);
  };

  const clearFilters = () => {
    onClearClick?.();
    setDraft(EMPTY_BANNER_LIBRARY_FILTER);
  };

  return (
    <>
      {isMobile ? (
        <div className="relative">
          <button
            type="button"
            aria-label={t.filterAriaLabel}
            onClick={openFilter}
            className="flex h-10 w-10 items-center justify-center rounded-lg bg-surface-brand-default"
          >
            <FilterIcon className="h-6 w-6" color="white" />
          </button>
          {activeFilterCount > 0 && (
            <div className="absolute -right-0.5 -top-0.5">
              <BadgeIcon className="h-2 w-2" />
            </div>
          )}
        </div>
      ) : (
        <ButtonV2 variant="tonal" onClick={openFilter}>
          <div className="flex items-center gap-2">
            <FilterIcon className="size-4 shrink-0" />
            {t.filterButton}
            {activeFilterCount > 0 && (
              <span className="flex h-4 w-4 items-center justify-center rounded-full bg-surface-brand-default text-[10px] font-semibold text-text-inverse-primary">
                {activeFilterCount}
              </span>
            )}
          </div>
        </ButtonV2>
      )}

      <ResponsiveModal
        isOpen={isOpen}
        onClose={closeFilter}
        isMobile={isMobile}
        title={t.filterModal.title}
        contentOverflow={isMobile ? "auto" : "visible"}
        footer={
          <div className="flex gap-2 lg:justify-end">
            <ButtonV2
              variant="outline"
              className="flex-1 lg:flex-none"
              onClick={clearFilters}
            >
              {t.filterModal.clear}
            </ButtonV2>
            <ButtonV2
              variant="brand"
              className="flex-1 lg:flex-none"
              onClick={applyFilters}
            >
              {t.filterModal.apply}
            </ButtonV2>
          </div>
        }
      >
        <div className="flex flex-col lg:gap-6 lg:pb-4 lg:pt-2">
          <MultiSelect
            id="banner-library-language-filter"
            options={languageOptions}
            selected={draft.language}
            label={t.filterModal.languageLabel}
            placeholder={t.filterModal.languagePlaceholder}
            variant={isMobile ? "expandable" : "dropdown"}
            className={isMobile ? "!pt-0" : undefined}
            onChange={(options) => {
              if (onLanguageChange) {
                trackMultiSelectChange(
                  draft.language,
                  options,
                  onLanguageChange,
                );
              }
              setDraft((prev) => ({ ...prev, language: options }));
            }}
          />

          <MultiSelect
            id="banner-library-banner-size-filter"
            options={sizeOptions}
            selected={draft.bannerSize}
            label={t.filterModal.bannerSizeLabel}
            placeholder={t.filterModal.bannerSizePlaceholder}
            variant={isMobile ? "expandable" : "dropdown"}
            hideDivider
            onChange={(options) => {
              if (onSizeChange) {
                trackMultiSelectChange(draft.bannerSize, options, onSizeChange);
              }
              setDraft((prev) => ({ ...prev, bannerSize: options }));
            }}
          />
        </div>
      </ResponsiveModal>
    </>
  );
};
